import { Component, OnInit } from '@angular/core';
import { AgilityRiskService } from '../../../../providers/agility-risk.service';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Threat } from '../../../../models/threat';
import { HttpErrorResponse } from '@angular/common/http';
import { AgilityRiskSpinnerService } from '../../../../providers/agility-risk-spinner.service';
import { TranslateService } from '@ngx-translate/core';
import { RiskTemplate } from '../../../../models/risk-template';
import { RiskTemplateTypes } from '../../../../models/risk-template-types';
import { ConfirmationModalComponent } from '../../../shared/modals/confirmation-modal/confirmation-modal.component';
import { Observable, of } from 'rxjs';
import { AgilityRiskMessageService } from '../../../../providers/agility-risk-message.service';

@Component({
  selector: 'lib-copy-threats-from-template',
  templateUrl: './copy-threats-from-template.component.html',
  styleUrls: ['./copy-threats-from-template.component.scss']
})
export class CopyThreatsFromTemplateComponent implements OnInit {

  public loading: boolean;
  public templatesList: RiskTemplate[];
  public threatsList: Threat[];
  public selectedThreats: Threat[];
  public selectedTemplateId: string;

  private destinationTemplateId: string;
  private destinationTemplate: RiskTemplate;

  constructor(private riskService: AgilityRiskService,
              private dialogService: DialogService,
              private dialogRef: DynamicDialogRef,
              private dialogConfig: DynamicDialogConfig,
              private spinner: AgilityRiskSpinnerService,
              private translateService: TranslateService,
              private agilityService: AgilityRiskService,
              private messageService: AgilityRiskMessageService) {
  }

  ngOnInit(): void {
    this.destinationTemplateId = this.dialogConfig.data.destinationTemplateId;
    this.loadTemplates();
  }

  templateSelectionChanged() {
    this.loadThreats();
  }

  loadTemplates() {
    this.spinner.start(this.translateService.instant('riskTemplates.msgLoadingTemplates'));
    this.riskService.getRiskTemplates().subscribe({
      next: res => {
        this.templatesList = res.content;
        // remove destination template from the list. toLowerCase is called in case we gave a guid id passed as a string for preparis
        const destIndex = this.templatesList.findIndex(t => t.id.toLowerCase() === this.destinationTemplateId.toLowerCase());
        this.destinationTemplate = this.templatesList.splice(destIndex, 1)[0];
        this.spinner.stop();
      },
      error: (error: HttpErrorResponse) => {
        console.error(error.message);
        this.spinner.stop();
      }
    });
  }

  loadThreats() {
    this.spinner.start(this.translateService.instant('riskTemplates.msgLoadingThreats'));
    this.riskService.getThreats(this.selectedTemplateId).subscribe({
      next: threats => {
        this.threatsList = threats;
        this.spinner.stop();
      },
      error: (error: HttpErrorResponse) => {
        console.error(error.message);
        this.spinner.stop();
      }
    });
  }

  onCopyThreats() {
    if (!this.destinationTemplateId) {
      this.messageService.error(this.translateService.instant('riskTemplates.msgInvalidDestinationTemplateId'));
      return;
    }

    this.checkSourceAndDestinationTemplateTypes().subscribe(
      res => {
        if (res) {
          this.copyThreat();
        }
      }
    );
  }

  copyThreat() {
    this.spinner.start();
    this.agilityService.saveTemplateThreats(this.destinationTemplateId, this.selectedThreats.map(th => th.id))
      .subscribe(
        {
          next: res => {
            this.messageService.success(this.translateService.instant('riskTemplates.msgCopiedThreatsToTemplate'));
            this.dialogRef.close(true);
            this.spinner.stop();
          },
          error: (error: HttpErrorResponse) => {
            this.messageService.error(this.translateService.instant('riskTemplates.msgFailedToCopyThreatsToTemplate'));
            this.spinner.stop();
          }
        }
      );
  }

  checkSourceAndDestinationTemplateTypes(): Observable<any> {
    const sourceTemplate = this.templatesList.find(t => t.id === this.selectedTemplateId);

    if (sourceTemplate.templateType === RiskTemplateTypes.overAllRisk &&
      this.destinationTemplate.templateType === RiskTemplateTypes.impactAndProbability) {

      const body1 = this.translateService.instant('riskTemplates.confirmThreatsCopy.body1');
      const body2 = this.translateService.instant('riskTemplates.confirmThreatsCopy.body2');
      const body3 = this.translateService.instant('riskTemplates.confirmThreatsCopy.body3');
      const body4 = this.translateService.instant('riskTemplates.confirmThreatsCopy.body4');

      const body = [body1, body2, body3, body4];
      const confirmationDynamicDialogRef = this.dialogService.open(ConfirmationModalComponent, {
        data: {
          body
        },
        header: this.translateService.instant('riskTemplates.confirmThreatsCopy.header'),
        width: '75%'
      });

      return confirmationDynamicDialogRef.onClose;
    }

    return of(true);
  }

  onCancel() {
    this.dialogRef.close();
  }
}
